import { Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DataTable from 'components/Common/DataTable';
import Spinner from 'components/Common/Spinner';
import { ColumnDef } from '@tanstack/react-table';
import { useGetAgencyUsageAnalytics, useGetAgencyUsageBreakdown, useGetBusinesses } from 'apis/agency/hook';
import { useState } from 'react';
import Pagination from 'components/Common/Pagination';
import { UsageBreakDownResponse } from 'apis/agency/types';
import { formatDuration, formatRevenue } from 'utils/helperFunctions';
import Select from 'components/Common/Select';
import DateRangePicker from 'components/Common/DateRangePicker';
import { useForm } from 'react-hook-form';
import { BusinessProfileResponse } from 'apis/business/types';
import { useGetAgencyConfiguration } from 'apis/agency/configuration/hook';
import ProfileIcon from 'assets/icons/ProfileIcon';

export default function Usage() {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: businesses, isLoading } = useGetBusinesses({ page: currentPage, filter: { account_status_eq: true } });

  const { control, watch } = useForm();
  const filters: Record<string, any> = watch();
  const dateArr = filters?.date_range?.split(',');
  const updatedFilters = {
    filter: {
      start_date: dateArr?.[0],
      end_date: dateArr?.[1],
      range_type: 'custom_range',
      business_id: filters?.business_id
    }
  };

  const { data: agencyConfigData } = useGetAgencyConfiguration();
  const { data: UsageBreakDownData, isLoading: isUsageBreakdownLoading } = useGetAgencyUsageBreakdown(updatedFilters);
  const { data: usageAnalyticsData, isLoading: isUsageAnalyticsLoading } = useGetAgencyUsageAnalytics(updatedFilters);

  if (isUsageBreakdownLoading || isUsageAnalyticsLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography variant='h6' mb={3} fontWeight={600}>
          Usage Analytics
        </Typography>
        <Box display='flex' gap={0.5}>
          <Box sx={{ minWidth: '270px' }}>
            <DateRangePicker name='date_range' control={control} />
          </Box>
          <Select
            name='business_id'
            label=''
            defaultValue={' '}
            placeholder='Business'
            control={control}
            options={[
              { value: ' ', label: 'All Business' },
              ...(businesses?.data.map((business: BusinessProfileResponse) => ({
                value: business.id,
                label: business.name
              })) ?? [])
            ]}
            sx={{ height: '39px', width: '150px', background: theme.palette.background.paper }}
          />
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid
          size={3}
          bgcolor='#fff'
          border='1px solid #ECECEC'
          paddingY={1.5}
          paddingX={2}
          borderRadius={1.5}
          borderLeft='3px solid rgb(59 130 246)'
        >
          <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
            <Typography color='secondary' fontWeight={500}>
              Total Business
            </Typography>
            <Box
              bgcolor='#DBEAFE'
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <BusinessIcon />
            </Box>
          </Box>
          <Typography variant='h5' mt={1} fontWeight={500}>
            {usageAnalyticsData?.total_businesses}
          </Typography>
          <Typography
            variant='body1'
            color='#878787'
            fontSize='0.75rem'
            fontWeight={500}
            borderTop='1px solid #ECECEC'
            mt={2}
            pt={1}
          >
            Active businesses using the platform
          </Typography>
        </Grid>
        <Grid
          size={3}
          bgcolor='#fff'
          border='1px solid #ECECEC'
          paddingY={1.5}
          paddingX={2}
          borderRadius={1.5}
          borderLeft='3px solid rgb(168 85 247)'
        >
          <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
            <Typography color='secondary' fontWeight={500}>
              Total Calls
            </Typography>
            <Box
              bgcolor='rgb(243 232 255)'
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <SmsIcon />
            </Box>
          </Box>
          <Typography variant='h5' mt={1} fontWeight={500}>
            {usageAnalyticsData?.total_calls}
          </Typography>
          <Typography
            variant='body1'
            color='#878787'
            fontSize='0.75rem'
            fontWeight={500}
            borderTop='1px solid #ECECEC'
            mt={2}
            pt={1}
          >
            Total number of calls processed
          </Typography>
        </Grid>
        <Grid
          size={3}
          bgcolor='#fff'
          border='1px solid #ECECEC'
          paddingY={1.5}
          paddingX={2}
          borderRadius={1.5}
          borderLeft='3px solid rgb(34 197 94)'
        >
          <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
            <Typography color='secondary' fontWeight={500}>
              Call Duration
            </Typography>
            <Box
              bgcolor='rgb(220 252 231)'
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <CallDurationIcon />
            </Box>
          </Box>
          <Typography variant='h5' mt={1} fontWeight={500}>
            {formatDuration(usageAnalyticsData?.total_duration as number)}
          </Typography>
          <Typography
            variant='body1'
            color='#878787'
            fontSize='0.75rem'
            fontWeight={500}
            borderTop='1px solid #ECECEC'
            mt={2}
            pt={1}
          >
            Total call duration
          </Typography>
        </Grid>
        {agencyConfigData?.billing_enabled ? (
          <Grid
            size={3}
            bgcolor='#fff'
            border='1px solid #ECECEC'
            paddingY={1.5}
            paddingX={2}
            borderRadius={1.5}
            borderLeft='3px solid rgb(245 158 11)'
          >
            <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
              <Typography color='secondary' fontWeight={500}>
                Total Revenue
              </Typography>
              <Box
                bgcolor='rgb(254 243 199)'
                borderRadius='50%'
                height='32px'
                width='32px'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <RevenueIcon />
              </Box>
            </Box>
            <Typography variant='h5' mt={1} fontWeight={500}>
              {formatRevenue(usageAnalyticsData?.total_revenue as number)}
            </Typography>
            <Typography
              variant='body1'
              color='#878787'
              fontSize='0.75rem'
              fontWeight={500}
              borderTop='1px solid #ECECEC'
              mt={2}
              pt={1}
            >
              Total revenue generated in USD
            </Typography>
          </Grid>
        ) : (
          <Grid
            size={3}
            bgcolor='#fff'
            border='1px solid #ECECEC'
            paddingY={1.5}
            paddingX={2}
            borderRadius={1.5}
            borderLeft='3px solid rgb(245 158 11)'
          >
            <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
              <Typography color='secondary' fontWeight={500}>
                Total Agents
              </Typography>
              <Box
                bgcolor='rgb(254 243 199)'
                color='rgb(245 158 11)'
                borderRadius='50%'
                height='32px'
                width='32px'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <ProfileIcon />
              </Box>
            </Box>
            <Typography variant='h5' mt={1} fontWeight={500}>
              {usageAnalyticsData?.total_agent}
            </Typography>
            <Typography
              variant='body1'
              color='#878787'
              fontSize='0.75rem'
              fontWeight={500}
              borderTop='1px solid #ECECEC'
              mt={2}
              pt={1}
            >
              Active Agents using the platform
            </Typography>
          </Grid>
        )}
      </Grid>

      <Typography variant='h2' fontWeight={600} mt={3} mb={1}>
        Usage Breakdown
      </Typography>
      <Typography variant='body2' mb={2}>
        Detailed usage by agent
      </Typography>
      <DataTable
        data={UsageBreakDownData?.data}
        columns={columns(agencyConfigData?.billing_enabled)}
        isHeaderSticky={true}
        isLoading={isLoading}
      />
      <Pagination totalPages={businesses?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
    </Box>
  );
}

const columns = (isBillingEnabled: boolean | undefined): ColumnDef<UsageBreakDownResponse>[] => [
  {
    header: 'Business',
    accessorKey: 'business_name'
  },
  {
    header: 'Agent Name',
    accessorKey: 'agent_name'
  },
  {
    header: 'Total Calls',
    accessorKey: 'total_calls'
  },
  {
    header: 'Call Duration',
    accessorKey: 'call_duration',
    cell: ({ getValue }) => formatDuration(getValue<number>())
  },
  ...(isBillingEnabled
    ? [
        {
          header: 'Total Revenue',
          accessorKey: 'total_revenue',
          cell: ({ getValue }: { getValue: any }) => formatRevenue(getValue() as number)
        }
      ]
    : [])
];

const BusinessIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
      style={{ color: 'rgb(59 130 246)', height: 20 }}
    >
      <rect width='16' height='20' x='4' y='2' rx='2' ry='2' />
      <path d='M9 22v-4h6v4' />
      <path d='M8 6h.01' />
      <path d='M16 6h.01' />
      <path d='M12 6h.01' />
      <path d='M12 10h.01' />
      <path d='M12 14h.01' />
      <path d='M16 10h.01' />
      <path d='M16 14h.01' />
      <path d='M8 10h.01' />
      <path d='M8 14h.01' />
    </svg>
  );
};

const SmsIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
      style={{ color: 'rgb(168 85 247)', height: 20 }}
    >
      <path d='M7.9 20A9 9 0 1 0 4 16.1L2 22Z' />
    </svg>
  );
};

const CallDurationIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
      style={{ color: 'rgb(34 197 94)', height: 20 }}
    >
      <circle cx='12' cy='12' r='10' />
      <polyline points='12 6 12 12 16 14' />
    </svg>
  );
};

const RevenueIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      stroke-width='2'
      stroke-linecap='round'
      stroke-linejoin='round'
      style={{ color: 'rgb(245 158 11)', height: 20 }}
    >
      <line x1='12' x2='12' y1='2' y2='22' />
      <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
    </svg>
  );
};
